import { Course, CourseItem, CourseSection, CourseTree, TemporaryId, COURSE_TYPE } from '@/entities/trainings';
import { courseService } from '@/services/courseService';
import { hasSections, isSection } from '@/ui/helpers/trainingsHelper';
import { defineStore } from 'pinia';

interface CourseBuilderState {
  id: number | null;
  course: CourseTree | null;
  isLoading: boolean;
  tempIdCounter: number;
  activeItem: CourseItem | null;
}

const defaultState = (): CourseBuilderState => ({
  id: null,
  course: null,
  isLoading: false,
  tempIdCounter: 0,
  activeItem: null,
});

export const useCourseBuilderStore = defineStore('courseBuilder', {
  state: defaultState,
  actions: {
    setId(id: number) {
      this.id = id;
    },

    updateCourseDetails(updated: Course) {
      if (!this.course) {
        return;
      }

      this.course = {
        ...this.course,
        title: updated.title,
        description: updated.description,
      };
    },

    async loadCourse(id: number) {
      this.isLoading = true;
      this.id = id;

      try {
        this.course = await courseService.load(id);
      } catch (error) {
        console.error('Failed to load course:', error);
        this.course = null;
      } finally {
        this.isLoading = false;
      }
    },

    newTempId(): TemporaryId {
      this.tempIdCounter += 1;
      return `temp-${this.tempIdCounter}`;
    },

    addSection(section?: CourseSection) {
      const pushIdx = section
        ? this.course!.items.indexOf(section) + 1
        : this.course!.items.length;

      this.course!.items.splice(pushIdx, 0, {
        id: this.newTempId(),
        type: COURSE_TYPE.SECTION,
        title: '',
        description: null,
        position: 0,
        section_id: null,
        items: [],
      });
    },

    addItem(section?: CourseSection) {
      const newItem: CourseItem = {
        id: this.newTempId(),
        type: COURSE_TYPE.TEXT,
        title: '',
        description: null,
        position: 0,
        section_id: section ? section.id : null,
      };

      if (section) {
        section.items.push(newItem);
      } else {
        this.course!.items.push(newItem);
      }

      this.setActiveItem(newItem);
    },

    removeItem(item: CourseItem, parent: CourseSection | CourseTree) {
      const idx = parent.items.findIndex(i => i.id === item.id);

      const activeItemBelongsToItem = this.activeItem && isSection(item) && item.items.includes(this.activeItem);
      if (this.activeItem === item || activeItemBelongsToItem) {
        this.setActiveItem(this.findItemBefore(item));
      }

      if (idx !== -1) {
        parent.items.splice(idx, 1);
      }
    },

    /**
     * Traverse the course items tree to find a component item before the target item
     * @param targetItem
     */
    findItemBefore(targetItem: CourseItem): CourseItem | null {
      let previousItem: CourseItem | null = null;

      this.course!.items.some(item => {
        if (item === targetItem) {
          return true;
        }

        if (isSection(item)) {
          return item.items.some(subItem => {
            if (subItem === targetItem) {
              return true;
            }
            previousItem = subItem;

            return false;
          });
        }

        previousItem = item;

        return false;
      });

      return previousItem;
    },

    setActiveItem(item: CourseItem | null) {
      this.activeItem = item;
    },

    updateItemTitle(item: CourseItem, title: string) {
      item.title = title;
    },

    updateItemDescription(item: CourseItem, description: string) {
      item.description = description;
    },

    enumeratePositionsAndSections() {
      if (!this.course) {
        return;
      }

      let position = 0;

      this.course.items.forEach(item => {
        item.position = position++;
        item.section_id = null;

        if (isSection(item)) {
          item.items.forEach(subItem => {
            subItem.position = position++;
            subItem.section_id = item.id;
          });
        }
      });
    },

    async save() {
      this.enumeratePositionsAndSections();

      this.isLoading = true;

      try {
        await courseService.update(this.id!, this.course!);
      } finally {
        this.isLoading = false;
      }
    },
  },
});
